import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";

import { attachClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import {
  attachInstruction,
  extractInstruction,
  Instruction,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/list-item";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { pointerOutsideOfPreview } from "@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview";
import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview";
import { SelectionType } from "@highnote/server/src/core/entities";
import { EntityRowConfig } from "App/components/EntityTable/config";
import classNames from "classnames";
import { CustomDropIndicator } from "./CustomDropIndicator";
import { DnDDragPreviewRow } from "./DnDDragPreviewRow";
import { useDnDMonitor } from "./DnDMonitor";
import { DragData, DropData } from "./types";

import styles from "./DnDWrapper.module.scss";

export interface DnDWrapperProps {
  children: React.ReactNode;
  // Draggable
  isDraggable?: boolean;
  dragData?: DragData;
  entityData?: EntityRowConfig; // Entity data for drag preview
  selectedEntities?: Record<string, SelectionType>; // Selection state for drag preview
  onDragStart?: () => void;
  onDragEnd?: () => void;

  // Drop target
  isDropTarget?: boolean;
  // For container wrappers, include { containerId }
  dropData?: DropData;
  canDrop?: (source: { data: DragData }) => boolean;
  onDrop?: (source: { data: DragData }) => void; // UI-side hook only (monitor handles logic)

  className?: string;
}

export const DnDWrapper = ({
  children,
  isDraggable,
  dragData,
  entityData,
  selectedEntities,
  onDragStart,
  onDragEnd,
  isDropTarget,
  dropData,
  canDrop,
  onDrop,
  className,
}: DnDWrapperProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [instruction, setInstruction] = useState<Instruction | null>(null);

  const { itemsMovingInDnDMonitor, isDnDMonitorLoading } = useDnDMonitor();

  const isRowCurrentlyMoving = useMemo(
    () => isDnDMonitorLoading && itemsMovingInDnDMonitor.has(dragData?.id),
    [isDnDMonitorLoading, itemsMovingInDnDMonitor.has(dragData?.id)],
  );

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const draggableAdapter =
      isDraggable && !isRowCurrentlyMoving
        ? draggable({
            element: el,
            getInitialData: () => dragData,
            onGenerateDragPreview: ({ nativeSetDragImage }) => {
              setCustomNativeDragPreview({
                getOffset: pointerOutsideOfPreview({ x: "16px", y: "8px" }),
                render: ({ container }) => {
                  ReactDOM.render(
                    <DnDDragPreviewRow
                      data={dragData}
                      entityData={entityData}
                      selectedEntities={selectedEntities}
                    />,
                    container,
                  );
                  return () => ReactDOM.unmountComponentAtNode(container);
                },
                nativeSetDragImage,
              });
            },
            onDragStart: () => {
              setIsDragging(true);
              onDragStart?.();
            },
            onDrop: () => {
              setIsDragging(false);
              onDragEnd?.();
            },
          })
        : null;

    const dropTargetAdapter =
      isDropTarget && !isRowCurrentlyMoving
        ? dropTargetForElements({
            element: el,
            getData: (args) => {
              const withEdge = attachClosestEdge(
                { ...dropData },
                {
                  input: args.input,
                  element: args.element,
                  allowedEdges: ["top", "bottom"],
                },
              );
              const withInstruction = attachInstruction(withEdge, {
                input: args.input,
                element: args.element,
                operations: {
                  "reorder-before": "available",
                  "reorder-after": "available",
                  combine: "available",
                },
              });
              const instr = extractInstruction(withInstruction);
              setInstruction(instr);
              return withInstruction;
            },
            canDrop: (args) => {
              return (
                canDrop?.(args.source as unknown as { data: DragData }) ?? true
              );
            },
            onDragLeave: () => {
              setInstruction(null);
            },
            onDrop: (args) => {
              setInstruction(null);
              onDrop?.(args.source as unknown as { data: DragData });
            },
          })
        : null;

    const cleanup = combine(
      draggableAdapter ?? (() => {}),
      dropTargetAdapter ?? (() => {}),
    );

    return cleanup;
  }, [
    isDraggable,
    isDropTarget,
    isRowCurrentlyMoving,
    dragData,
    entityData,
    dropData,
    onDragStart,
    onDragEnd,
    canDrop,
    onDrop,
  ]);

  return (
    <div
      ref={ref}
      className={classNames(
        styles.container,
        {
          [styles.dragging]: isDragging,
          [styles.draggable]: isDraggable && !isDragging,
          [styles.moving]: isRowCurrentlyMoving,
        },
        className,
      )}
    >
      {children}
      {instruction && <CustomDropIndicator instruction={instruction} />}
    </div>
  );
};
